import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGuard, useAuth } from '@/features/auth';
import { useUserQuery } from '../hooks/useUserQuery';
import { UserRolesPanel } from '../components/UserRolesPanel';
import { UserPermissionsPanel } from '../components/UserPermissionsPanel';
import { UserMfaPanel } from '../components/UserMfaPanel';
import { UserSessionsPanel } from '../components/UserSessionsPanel';
import { UserStatusPanel } from '../components/UserStatusPanel';
import { UserAssociationPanel } from '../components/UserAssociationPanel';
import { UserAuditPanel } from '../components/UserAuditPanel';
import { UserSecurityGroupsPanel } from '../components/UserSecurityGroupsPanel';
import { UserRound } from 'lucide-react';
import { Alert, Badge, ButtonLink, DetailPageHeader, PageContainer, TabList, TabPanel, TabsRoot } from '@/shared/components';
import { UserForgotPasswordPanel } from '../components/UserForgotPasswordPanel';
import { HomeDealerLink } from '../components/HomeDealerLink';

const DETAIL_ROW_CLASSES = 'border-b border-mp-border';
const DETAIL_HEADER_CLASSES = 'px-3 py-2 text-left';
const DETAIL_CELL_CLASSES = 'px-3 py-2 text-left';

type Tab = 'identity' | 'roles' | 'permissions' | 'security-groups' | 'mfa' | 'association' | 'audit';

const TABS: { id: Tab; label: string }[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'roles', label: 'Roles' },
  { id: 'permissions', label: 'Permissions' },
  { id: 'security-groups', label: 'Security Groups' },
  { id: 'mfa', label: 'MFA & Sessions' },
  { id: 'association', label: 'Association' },
  { id: 'audit', label: 'Audit' },
];

export function UserDetail() {
  const { user: loggedInUser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('identity');

  const userQuery = useUserQuery(id ?? '');

  if (!id) {
    return <PageContainer><p className="text-mp-error">Invalid user ID.</p></PageContainer>;
  }

  if (userQuery.isLoading) {
    return <PageContainer><p>Loading user...</p></PageContainer>;
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <PageContainer>
        <Alert variant="error">
          <span>User not found or failed to load.</span>
        </Alert>
      </PageContainer>
    );
  }

  const user = userQuery.data;

  const title = user.name || user.email;

  return (
    <TabsRoot
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      idPrefix="user-detail"
      ariaLabel="User sections"
      variant="header"
    >
      <DetailPageHeader
        breadcrumbs={[{ label: 'Users', to: '/admin/users' }, { label: title }]}
        title={title}
        icon={UserRound}
        badges={
          <Badge size="xs" variant={user.status === 'active' ? 'success' : 'error'}>
            {user.status}
          </Badge>
        }
        actions={
          <PermissionGuard permission={{ resource: 'user', action: 'edit' }}>
            <ButtonLink to={`/admin/users/${id}/edit`} variant="neutral" appearance="outline" size="sm">
              Edit User
            </ButtonLink>
          </PermissionGuard>
        }
        tabs={<TabList />}
      />
      <PageContainer>
        <TabPanel>
          {activeTab === 'identity' && (
            <div className="overflow-x-auto space-y-8">
              <table className="w-full text-sm max-w-2xl">
                <tbody>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={`w-40 ${DETAIL_HEADER_CLASSES}`}>ID</th>
                    <td className={`${DETAIL_CELL_CLASSES} font-mono text-xs`}>{user.id}</td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Email</th>
                    <td className={`${DETAIL_CELL_CLASSES} font-semibold`}>
                      {user.email != user.email_lower
                        ? `Email Released`
                        : user.email}
                    </td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Name</th>
                    <td className={DETAIL_CELL_CLASSES}>{user.name}</td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Status</th>
                    <td className={DETAIL_CELL_CLASSES}>
                      <Badge size="xs" variant={user.status === 'active' ? 'success' : 'error'}>
                        {user.status}
                      </Badge>
                    </td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>MFA Enforced</th>
                    <td className={DETAIL_CELL_CLASSES}>{user.mfa_enforced ? 'Yes' : 'No'}</td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Home Dealer</th>
                    <td className={DETAIL_CELL_CLASSES}><HomeDealerLink dealerId={user.dealer_id} /></td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Organisation ID</th>
                    <td className={`${DETAIL_CELL_CLASSES} font-mono text-xs`}>{user.organisation_id ?? <span className="text-mp-muted">—</span>}</td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Email Verified</th>
                    <td className={DETAIL_CELL_CLASSES}>
                      {user.email_verified_at
                        ? new Date(user.email_verified_at).toLocaleString()
                        : <span className="text-mp-muted">Not verified</span>}
                    </td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Last Sign In</th>
                    <td className={DETAIL_CELL_CLASSES}>
                      {user.last_sign_in_at
                        ? new Date(user.last_sign_in_at).toLocaleString()
                        : <span className="text-mp-muted">Never</span>}
                    </td>
                  </tr>
                  <tr className={DETAIL_ROW_CLASSES}>
                    <th className={DETAIL_HEADER_CLASSES}>Created</th>
                    <td className={DETAIL_CELL_CLASSES}>{new Date(user.created_at).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
              <div className="border-t border-mp-border pt-6 max-w-2xl">
                <UserForgotPasswordPanel email={user.email} />
              </div>
              {loggedInUser?.id !== user.id && (
                <div className="border-t border-mp-border pt-6 max-w-2xl">
                  <UserStatusPanel userId={id} status={user.status} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'roles' && <UserRolesPanel userId={id} />}

          {activeTab === 'permissions' && <UserPermissionsPanel userId={id} />}

          {activeTab === 'security-groups' && (
            <UserSecurityGroupsPanel userId={id} userName={title} />
          )}

          {activeTab === 'mfa' && (
            <div className="space-y-8 max-w-2xl">
              <UserMfaPanel userId={id} user={user} />
              <div className="border-t border-mp-border pt-6">
                <UserSessionsPanel userId={id} />
              </div>
            </div>
          )}

          {activeTab === 'association' && <UserAssociationPanel userId={id} user={user} />}

          {activeTab === 'audit' && <UserAuditPanel userId={id} />}
        </TabPanel>
      </PageContainer>
    </TabsRoot>
  );
}
